import type { ReactNode } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import { LoadingState } from '@/components/ui/Feedback';
import { dashboardItem, navigation } from '@/constants/navigation';
import { useAuthStore } from '@/store/auth.store';

export interface ProtectedRouteProps {
  children: ReactNode;
  permission?: string;
}

export function ProtectedRoute({ children, permission }: ProtectedRouteProps) {
  const user = useAuthStore((state) => state.user);
  const isInitialising = useAuthStore((state) => state.isInitialising);
  const can = useAuthStore((state) => state.can);
  const location = useLocation();

  if (isInitialising) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Checking your session…" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (permission && !can(permission)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

export function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isInitialising = useAuthStore((state) => state.isInitialising);

  if (isInitialising) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Loading…" />
      </div>
    );
  }

  return user ? <Navigate to="/" replace /> : <>{children}</>;
}

export function HomeRoute({ children }: { children: ReactNode }) {
  const can = useAuthStore((state) => state.can);

  if (can(dashboardItem.permission)) return <>{children}</>;

  const first = navigation.flatMap((section) => section.items).find((item) => can(item.permission));
  return <Navigate to={first?.to ?? '/forbidden'} replace />;
}
